import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { ShieldCheck, X, AlertTriangle, UserPlus, HelpCircle, RotateCcw } from 'lucide-react';

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

interface ModalBaseProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const ModalBase: React.FC<ModalBaseProps> = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    if (!isOpen) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = orig;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-border bg-card text-foreground p-6 shadow-2xl space-y-4 animate-in zoom-in-95 relative z-50 my-auto font-poppins"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border/60 pb-3">
          <h3 className="text-base font-bold text-foreground flex items-center gap-2 font-poppins">{title}</h3>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div>{children}</div>
      </div>
    </div>,
    document.body
  );
};

// 1. Approve Confirm Modal
export const ApproveModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  nfaNumber: string;
  amount: number;
  currency?: string;
  onConfirm: (comments: string) => Promise<void>;
}> = ({ isOpen, onClose, nfaNumber, amount, onConfirm }) => {
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await onConfirm(comments);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} title={`Authorize ${nfaNumber}`}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs font-poppins">
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3.5 text-emerald-800 dark:text-emerald-300 flex items-start gap-2.5">
          <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5 text-emerald-600" />
          <div>
            <span className="font-bold text-sm block">Authorizing Commitment: {formatInr(amount)}</span>
            <p className="text-[11px] mt-0.5 text-foreground-secondary">
              This action executes official managerial authorization and records an immutable audit signature for Purchase Order release.
            </p>
          </div>
        </div>

        <div>
          <label className="block font-bold text-foreground mb-1">Approval Remarks / Audit Notes (Optional)</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Enter any approval conditions, milestone notes, or delivery directives..."
            className="w-full rounded-xl border border-input bg-background p-3 text-xs text-foreground focus:ring-2 focus:ring-emerald-500 h-20 font-poppins"
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting} className="font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button type="submit" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold font-poppins cursor-pointer" isLoading={isSubmitting}>
            Confirm Authorization
          </Button>
        </div>
      </form>
    </ModalBase>
  );
};

// 2. Reject Modal
export const RejectModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  nfaNumber: string;
  onConfirm: (reason: string, comments: string) => Promise<void>;
}> = ({ isOpen, onClose, nfaNumber, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [comments, setComments] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Rejection reason is required by enterprise policy.');
      return;
    }
    try {
      setIsSubmitting(true);
      await onConfirm(reason, comments);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} title={`Reject ${nfaNumber}`}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs font-poppins">
        <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3.5 text-rose-800 dark:text-rose-300 flex items-start gap-2.5">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-rose-600" />
          <p className="text-[11px] text-foreground-secondary leading-relaxed">
            Rejection immediately halts the commercial approval chain and logs a permanent rejection audit notice for the requisitioner.
          </p>
        </div>

        <div>
          <label className="block font-bold text-foreground mb-1">Mandatory Rejection Reason *</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError('');
            }}
            placeholder="e.g. Budget ceiling exceeded, non-compliant supplier, unapproved terms..."
            className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground focus:ring-2 focus:ring-rose-500 font-poppins"
          />
          {error && <p className="text-[10px] text-rose-500 font-bold mt-1">{error}</p>}
        </div>

        <div>
          <label className="block font-bold text-foreground mb-1">Detailed Explanation & Guidance</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Provide specific details or alternative recommendations for the procurement team..."
            className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground focus:ring-2 focus:ring-rose-500 h-20 font-poppins"
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting} className="font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button type="submit" size="sm" variant="destructive" className="font-bold font-poppins cursor-pointer" isLoading={isSubmitting}>
            Confirm Rejection
          </Button>
        </div>
      </form>
    </ModalBase>
  );
};

// 3. Send Back Modal
export const SendBackModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  nfaNumber: string;
  onConfirm: (reason: string, targetStage: number) => Promise<void>;
}> = ({ isOpen, onClose, nfaNumber, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [targetStage, setTargetStage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    try {
      setIsSubmitting(true);
      await onConfirm(reason, targetStage);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} title={`Return ${nfaNumber} for Revision`}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs font-poppins">
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3.5 text-amber-800 dark:text-amber-300 flex items-start gap-2.5">
          <RotateCcw className="h-5 w-5 shrink-0 mt-0.5 text-amber-600" />
          <p className="text-[11px] text-foreground-secondary leading-relaxed">
            Return the dossier to the buyer or requisitioner for updated quotes, modified quantities, or revised commercial terms.
          </p>
        </div>

        <div>
          <label className="block font-bold text-foreground mb-1">Target Revision Stage</label>
          <select
            value={targetStage}
            onChange={(e) => setTargetStage(Number(e.target.value))}
            className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-poppins"
          >
            <option value={1}>Stage 1: Requisitioner / Procurement Buyer Revision</option>
          </select>
        </div>

        <div>
          <label className="block font-bold text-foreground mb-1">Correction Instructions *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Specify what changes, revised quotation comparisons, or attachments are required..."
            className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground h-20 font-poppins"
            required
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting} className="font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button type="submit" size="sm" className="bg-amber-600 hover:bg-amber-700 text-white font-bold font-poppins cursor-pointer" isLoading={isSubmitting}>
            Return for Revision
          </Button>
        </div>
      </form>
    </ModalBase>
  );
};

// 4. Clarification Modal
export const ClarificationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  nfaNumber: string;
  onConfirm: (question: string, dueDate: string) => Promise<void>;
}> = ({ isOpen, onClose, nfaNumber, onConfirm }) => {
  const [question, setQuestion] = useState('');
  const [dueDate, setDueDate] = useState(
    new Date(Date.now() + 48 * 3600000).toISOString().split('T')[0]!
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;
    try {
      setIsSubmitting(true);
      await onConfirm(question, dueDate);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} title={`Request Clarification — ${nfaNumber}`}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs font-poppins">
        <div className="rounded-xl bg-indigo-500/10 border border-indigo-500/30 p-3.5 text-indigo-800 dark:text-indigo-300 flex items-start gap-2.5">
          <HelpCircle className="h-5 w-5 shrink-0 mt-0.5 text-indigo-600" />
          <p className="text-[11px] text-foreground-secondary leading-relaxed">
            Query the procurement buyer without stopping the overall workflow.
          </p>
        </div>

        <div>
          <label className="block font-bold text-foreground mb-1">Query / Question *</label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Type your question regarding quotation scoring, vendor delivery commitments, or payment milestones..."
            className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground h-20 font-poppins"
            required
          />
        </div>

        <div>
          <label className="block font-bold text-foreground mb-1">Response Due Date</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-poppins"
            required
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting} className="font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold font-poppins cursor-pointer" isLoading={isSubmitting}>
            Submit Query
          </Button>
        </div>
      </form>
    </ModalBase>
  );
};

// 5. Delegate Modal
export const DelegateModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  nfaNumber: string;
  onConfirm: (dto: { toUserId: string; toUserName: string; toUserRole: string; startDate: string; endDate: string; reason: string }) => Promise<void>;
}> = ({ isOpen, onClose, nfaNumber, onConfirm }) => {
  const [toUserId, setToUserId] = useState('usr-3');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]!);
  const [endDate, setEndDate] = useState(new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0]!);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableDelegates = [
    { id: 'usr-3', name: 'David Sterling', role: 'Procurement Manager' },
    { id: 'usr-5', name: 'Alexander Hamilton', role: 'VP Operations' },
    { id: 'usr-2', name: 'Rachel Chen', role: 'Finance Controller' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    const target = availableDelegates.find((d) => d.id === toUserId) || availableDelegates[0]!;
    try {
      setIsSubmitting(true);
      await onConfirm({
        toUserId: target.id,
        toUserName: target.name,
        toUserRole: target.role,
        startDate,
        endDate,
        reason,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} title={`Delegate Authority — ${nfaNumber}`}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs font-poppins">
        <div className="rounded-xl bg-purple-500/10 border border-purple-500/30 p-3.5 text-purple-800 dark:text-purple-300 flex items-start gap-2.5">
          <UserPlus className="h-5 w-5 shrink-0 mt-0.5 text-purple-600" />
          <p className="text-[11px] text-foreground-secondary leading-relaxed">
            Temporarily assign financial approval delegation to a designated authority during planned absence.
          </p>
        </div>

        <div>
          <label className="block font-bold text-foreground mb-1">Select Delegate Authority *</label>
          <select
            value={toUserId}
            onChange={(e) => setToUserId(e.target.value)}
            className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-semibold font-poppins"
          >
            {availableDelegates.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.role})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-bold text-foreground mb-1">Effective Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-poppins"
              required
            />
          </div>
          <div>
            <label className="block font-bold text-foreground mb-1">Effective End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-poppins"
              required
            />
          </div>
        </div>

        <div>
          <label className="block font-bold text-foreground mb-1">Delegation Justification *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Executive travel coverage, planned medical leave..."
            className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground h-16 font-poppins"
            required
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting} className="font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button type="submit" size="sm" className="bg-purple-600 hover:bg-purple-700 text-white font-bold font-poppins cursor-pointer" isLoading={isSubmitting}>
            Confirm Delegation
          </Button>
        </div>
      </form>
    </ModalBase>
  );
};
